import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Upload, Camera, Link, Search, X, Maximize2, Check, Loader, Image as ImageIcon, ArrowLeft } from 'lucide-react';
import { getAuthHeaders } from '../../context/AuthContext';
import { compressImage } from '../../lib/imageUtils';
import { resolveImageUrl } from '../../utils/imageUrl';

import { BACKEND_URL } from '../../config/api';

function Lightbox({ src, onClose }) {
  if (!src) return null;
  return (
    <div
      className="fixed inset-0 bg-charcoal/80 flex items-center justify-center z-[80] p-4 cursor-pointer"
      onClick={onClose}
      data-testid="change-image-lightbox"
    >
      <div className="relative max-w-3xl w-full max-h-[85vh] flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
        <img src={src} alt="Preview" className="max-w-full max-h-[85vh] object-contain rounded-xl shadow-2xl" />
        <button onClick={onClose} className="absolute top-3 right-3 p-2 bg-white/90 rounded-full hover:bg-white transition-colors shadow-lg" data-testid="close-change-lightbox">
          <X size={18} />
        </button>
      </div>
    </div>
  );
}

function ImageGrid({ images, selected, onSelect, onPreview }) {
  if (!images.length) return null;
  return (
    <div className="grid grid-cols-3 gap-2 mt-3" data-testid="image-grid">
      {images.map((img, idx) => (
        <div key={idx} className="relative group">
          <button
            type="button"
            onClick={() => onSelect(img)}
            className={`relative w-full aspect-square rounded-lg overflow-hidden bg-gray-100 ${
              selected?.url === img.url ? 'ring-2 ring-sage ring-offset-2' : 'hover:ring-2 hover:ring-sage/50'
            } transition-all`}
            data-testid={`change-img-option-${idx}`}
          >
            <img src={img.url} alt={img.alt || ''} className="w-full h-full object-cover" onError={(e) => { e.target.parentElement.style.display = 'none'; }} />
            {selected?.url === img.url && (
              <div className="absolute top-1.5 right-1.5 w-6 h-6 bg-sage rounded-full flex items-center justify-center shadow-sm">
                <Check size={14} className="text-white" />
              </div>
            )}
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onPreview(img.url); }}
            className="absolute bottom-1.5 right-1.5 p-1.5 bg-white/90 rounded-lg shadow-sm hover:bg-white transition-all opacity-80 sm:opacity-0 sm:group-hover:opacity-100"
            data-testid={`change-img-preview-${idx}`}
          >
            <Maximize2 size={14} className="text-charcoal" />
          </button>
        </div>
      ))}
    </div>
  );
}

export default function ChangeImageFlow({ onImageSelected, onCancel, itemName, itemBrand }) {
  const [tab, setTab] = useState('snap');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const fileInputRef = useRef(null);

  // Upload tab
  const [isDragOver, setIsDragOver] = useState(false);

  // Snap & Search tab
  const [snapFile, setSnapFile] = useState(null);
  const [snapPreview, setSnapPreview] = useState(null);
  const [aiResult, setAiResult] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const snapInputRef = useRef(null);

  // URL tab
  const [urlInput, setUrlInput] = useState('');
  const [scrapedImages, setScrapedImages] = useState([]);
  const [selectedScraped, setSelectedScraped] = useState(null);

  // Shared: upload file, compress, send to backend (no auto-detection)
  const uploadAndProcess = async (file) => {
    const compressed = await compressImage(file);
    const formData = new FormData();
    formData.append('file', compressed);
    formData.append('detect_mode', 'none');

    const resp = await fetch(`${BACKEND_URL}/api/stash/upload-image`, {
      method: 'POST',
      credentials: 'include',
      headers: getAuthHeaders(),
      body: formData,
    });

    if (!resp.ok) throw new Error('Upload failed');
    return resp.json();
  };

  // Upload a remote image URL: use backend to download, store, process (avoids CORS)
  const uploadFromUrl = async (imageUrl) => {
    const formData = new FormData();
    formData.append('image_url', imageUrl);
    formData.append('detect_mode', 'none');

    const resp = await fetch(`${BACKEND_URL}/api/stash/download-image`, {
      method: 'POST',
      credentials: 'include',
      headers: getAuthHeaders(),
      body: formData,
    });

    if (!resp.ok) {
      const err = await resp.json().catch(() => ({}));
      throw new Error(err.detail || 'Failed to download image');
    }
    return resp.json();
  };

  // ===== UPLOAD TAB =====
  const handleFileUpload = async (file) => {
    if (!file || !file.type.startsWith('image/')) return;
    setLoading(true);
    setError(null);
    try {
      const result = await uploadAndProcess(file);
      onImageSelected(result);
    } catch (e) {
      setError('Failed to upload image. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(false);
    handleFileUpload(e.dataTransfer.files[0]);
  }, []);

  // ===== SNAP & SEARCH TAB =====
  const handleSnapCapture = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setSnapFile(file);
    setSnapPreview(URL.createObjectURL(file));
    setAiResult(null);
    setSelectedImage(null);
  };

  // Auto-analyze after snap capture
  useEffect(() => {
    if (snapFile && !aiResult && !loading) {
      handleSnapAnalyze();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snapFile]);

  const handleSnapAnalyze = async () => {
    if (!snapFile) return;
    setLoading(true);
    setError(null);
    try {
      const compressed = await compressImage(snapFile);
      const formData = new FormData();
      formData.append('image', compressed);
      formData.append('detect_mode', 'both');

      const resp = await fetch(`${BACKEND_URL}/api/stash/lookup-image`, {
        method: 'POST',
        credentials: 'include',
        headers: getAuthHeaders(),
        body: formData,
      });
      if (!resp.ok) throw new Error('Analysis failed');
      const data = await resp.clone().json();
      setAiResult(data);
      // Pre-select the uploaded image if available
      if (data.uploaded_image_url) {
        setSelectedImage({ url: resolveImageUrl(data.uploaded_image_url), source: 'uploaded' });
      }
    } catch (e) {
      setError('Failed to analyze image. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSnapConfirm = async () => {
    if (!selectedImage) return;
    setLoading(true);
    setError(null);
    try {
      if (selectedImage.source === 'uploaded' && aiResult?.uploaded_image_url) {
        // Use the already-uploaded image
        onImageSelected({
          image_url: aiResult.uploaded_image_url,
          thumbnail_url: aiResult.uploaded_image_url,
          colors: aiResult.detected?.colors || [],
          sentiments: aiResult.detected?.sentiments || [],
          primary_color: aiResult.detected?.colors?.[0] || null,
          secondary_color: aiResult.detected?.colors?.[1] || null,
        });
      } else {
        // Download the selected web image and process it
        const result = await uploadFromUrl(selectedImage.url);
        onImageSelected(result);
      }
    } catch (e) {
      setError('Failed to save image. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ===== URL TAB =====
  const handleUrlSearch = async () => {
    if (!urlInput.trim()) return;
    setLoading(true);
    setError(null);
    setScrapedImages([]);
    setSelectedScraped(null);
    try {
      const imageExts = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp'];
      const isDirectImage = imageExts.some(ext => urlInput.toLowerCase().includes(ext));

      if (isDirectImage) {
        const direct = { url: urlInput, alt: 'Direct image', source: 'direct' };
        setScrapedImages([direct]);
        setSelectedScraped(direct);
      } else {
        const resp = await fetch(`${BACKEND_URL}/api/images/scrape-url`, {
          method: 'POST',
          credentials: 'include',
          headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({ url: urlInput }),
        });
        if (!resp.ok) {
          const err = await resp.clone().json();
          throw new Error(err.detail || 'Failed to fetch URL');
        }
        const data = await resp.clone().json();
        const images = data.images || [];
        setScrapedImages(images);
        if (images.length) {
          setSelectedScraped(images[0]);
        } else {
          setError('No product images found on this page.');
        }
      }
    } catch (e) {
      setError(e.message || 'Failed to fetch images from URL.');
    } finally {
      setLoading(false);
    }
  };

  const handleUrlConfirm = async () => {
    if (!selectedScraped) return;
    setLoading(true);
    setError(null);
    try {
      const result = await uploadFromUrl(selectedScraped.url);
      onImageSelected(result);
    } catch (e) {
      setError('Failed to save image. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'snap', label: 'Snap & Search', icon: Camera },
    { id: 'url', label: 'URL Lookup', icon: Link },
    { id: 'upload', label: 'Upload', icon: Upload },
  ];

  return (
    <div data-testid="change-image-flow">
      <Lightbox src={previewUrl} onClose={() => setPreviewUrl(null)} />

      {/* Header */}
      <div className="flex items-center gap-2 mb-4">
        <button type="button" onClick={onCancel} className="p-1.5 rounded-lg hover:bg-beige transition-colors" data-testid="change-image-back">
          <ArrowLeft size={18} className="text-charcoal" />
        </button>
        <h3 className="text-sm font-medium text-charcoal">Change Image</h3>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-cream rounded-lg p-1 mb-4" data-testid="change-image-tabs">
        {tabs.map(t => (
          <button
            key={t.id}
            type="button"
            onClick={() => { setTab(t.id); setError(null); }}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-2 rounded-md text-xs font-medium transition-all ${
              tab === t.id ? 'bg-white text-charcoal shadow-sm' : 'text-gray-soft hover:text-charcoal'
            }`}
            data-testid={`change-tab-${t.id}`}
          >
            <t.icon size={14} />
            {t.label}
          </button>
        ))}
      </div>

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 px-3 py-2 bg-red-50 border border-red-200 text-red-700 rounded-lg text-xs mb-3" data-testid="change-image-error">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="ml-auto"><X size={12} /></button>
        </div>
      )}

      {/* Loading overlay */}
      {loading && (
        <div className="flex items-center justify-center py-8">
          <Loader className="animate-spin text-sage" size={28} />
          <span className="ml-2 text-sm text-gray-soft">Processing...</span>
        </div>
      )}

      {/* ===== UPLOAD TAB ===== */}
      {tab === 'upload' && !loading && (
        <div
          onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
          onDragLeave={(e) => { e.preventDefault(); setIsDragOver(false); }}
          onDrop={handleDrop}
          className={`border-2 border-dashed rounded-lg p-6 text-center transition-all ${
            isDragOver ? 'border-sage bg-sage/5' : 'border-beige hover:border-sage'
          }`}
          data-testid="change-upload-drop"
        >
          <div className={`mx-auto w-12 h-12 rounded-full flex items-center justify-center mb-3 ${isDragOver ? 'bg-sage/20' : 'bg-beige'}`}>
            {isDragOver ? <ImageIcon size={24} className="text-sage" /> : <Upload size={24} className="text-gray-light" />}
          </div>
          <p className="text-sm text-gray-soft mb-2">{isDragOver ? 'Drop image here' : 'Drag & drop an image'}</p>
          <p className="text-xs text-gray-light mb-3">or</p>
          <input ref={fileInputRef} type="file" accept="image/*" onChange={(e) => handleFileUpload(e.target.files[0])} className="hidden" data-testid="change-upload-input" />
          <button type="button" onClick={() => fileInputRef.current?.click()} className="px-4 py-2 bg-sage text-white text-sm rounded-lg hover:bg-sage-dark transition-colors" data-testid="change-upload-browse">
            Browse Files
          </button>
        </div>
      )}

      {/* ===== SNAP & SEARCH TAB ===== */}
      {tab === 'snap' && !loading && (
        <div className="space-y-3">
          {!aiResult ? (
            <>
              {snapPreview ? (
                <div className="text-center">
                  <img src={snapPreview} alt="Captured" className="max-h-40 mx-auto rounded-lg mb-3" />
                  <div className="flex gap-2 justify-center">
                    <button type="button" onClick={() => { setSnapFile(null); setSnapPreview(null); }} className="px-3 py-2 text-xs border border-beige rounded-lg hover:border-sage transition-colors" data-testid="snap-retake">
                      Retake
                    </button>
                    <button type="button" onClick={handleSnapAnalyze} className="px-4 py-2 text-xs bg-sage text-white rounded-lg hover:bg-sage-dark transition-colors flex items-center gap-1.5" data-testid="snap-analyze">
                      <Search size={14} />
                      Find Product
                    </button>
                  </div>
                </div>
              ) : (
                <div className="text-center py-4">
                  <div className="mx-auto w-12 h-12 rounded-full bg-beige flex items-center justify-center mb-3">
                    <Camera size={24} className="text-gray-light" />
                  </div>
                  <p className="text-sm text-gray-soft mb-3">Take or upload a photo to search</p>
                  <input ref={snapInputRef} type="file" accept="image/*" capture="environment" onChange={handleSnapCapture} className="hidden" data-testid="snap-input" />
                  <button type="button" onClick={() => snapInputRef.current?.click()} className="px-4 py-2 bg-sage text-white text-sm rounded-lg hover:bg-sage-dark transition-colors" data-testid="snap-capture-btn">
                    Take Photo / Browse
                  </button>
                </div>
              )}
            </>
          ) : (
            <>
              {/* AI Results */}
              {aiResult.detected?.name && (
                <p className="text-xs text-gray-soft">
                  Detected: <span className="font-medium text-charcoal">{aiResult.detected.name}</span>
                  {aiResult.detected.brand && <span> by {aiResult.detected.brand}</span>}
                </p>
              )}

              {/* Image options */}
              <p className="text-xs text-gray-soft font-medium">Select an image:</p>
              <div className="grid grid-cols-3 gap-2">
                {/* Uploaded photo */}
                {snapPreview && (
                  <div className="relative group">
                    <button type="button" onClick={() => setSelectedImage({ url: resolveImageUrl(aiResult.uploaded_image_url), source: 'uploaded' })}
                      className={`w-full aspect-square rounded-lg overflow-hidden bg-gray-100 ${selectedImage?.source === 'uploaded' ? 'ring-2 ring-sage ring-offset-2' : 'hover:ring-2 hover:ring-sage/50'} transition-all`}
                      data-testid="snap-select-photo"
                    >
                      <img src={snapPreview} alt="Your photo" className="w-full h-full object-cover" />
                      {selectedImage?.source === 'uploaded' && <div className="absolute top-1.5 right-1.5 w-5 h-5 bg-sage rounded-full flex items-center justify-center"><Check size={12} className="text-white" /></div>}
                    </button>
                    <button type="button" onClick={() => setPreviewUrl(snapPreview)} className="absolute bottom-1.5 right-1.5 p-1 bg-white/90 rounded-md shadow-sm hover:bg-white opacity-80 sm:opacity-0 sm:group-hover:opacity-100 transition-all" data-testid="snap-preview-photo">
                      <Maximize2 size={12} className="text-charcoal" />
                    </button>
                    <p className="text-[10px] text-gray-soft text-center mt-1">Your Photo</p>
                  </div>
                )}
                {/* Web images */}
                {aiResult.suggested_images?.map((img, idx) => (
                  <div key={idx} className="relative group">
                    <button type="button" onClick={() => setSelectedImage({ ...img, source: 'web' })}
                      className={`w-full aspect-square rounded-lg overflow-hidden bg-gray-100 ${selectedImage?.source === 'web' && selectedImage?.url === img.url ? 'ring-2 ring-sage ring-offset-2' : 'hover:ring-2 hover:ring-sage/50'} transition-all`}
                      data-testid={`snap-select-web-${idx}`}
                    >
                      <img src={img.thumbnail || img.url} alt={img.name || ''} className="w-full h-full object-cover" onError={(e) => { e.target.parentElement.parentElement.style.display = 'none'; }} />
                      {selectedImage?.source === 'web' && selectedImage?.url === img.url && <div className="absolute top-1.5 right-1.5 w-5 h-5 bg-sage rounded-full flex items-center justify-center"><Check size={12} className="text-white" /></div>}
                    </button>
                    <button type="button" onClick={() => setPreviewUrl(img.url)} className="absolute bottom-1.5 right-1.5 p-1 bg-white/90 rounded-md shadow-sm hover:bg-white opacity-80 sm:opacity-0 sm:group-hover:opacity-100 transition-all" data-testid={`snap-preview-web-${idx}`}>
                      <Maximize2 size={12} className="text-charcoal" />
                    </button>
                  </div>
                ))}
              </div>

              <div className="flex gap-2 pt-2">
                <button type="button" onClick={() => { setAiResult(null); setSnapFile(null); setSnapPreview(null); setSelectedImage(null); }}
                  className="flex-1 py-2 text-xs border border-beige rounded-lg hover:border-sage transition-colors" data-testid="snap-start-over">
                  Start Over
                </button>
                <button type="button" onClick={handleSnapConfirm} disabled={!selectedImage}
                  className="flex-1 py-2 text-xs bg-sage text-white rounded-lg hover:bg-sage-dark transition-colors disabled:opacity-50" data-testid="snap-confirm">
                  Use This Image
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* ===== URL TAB ===== */}
      {tab === 'url' && !loading && (
        <div className="space-y-3">
          <div className="flex gap-2">
            <input
              type="text"
              value={urlInput}
              onChange={(e) => setUrlInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleUrlSearch())}
              placeholder="Paste image URL or product page URL..."
              className="flex-1 px-3 py-2 text-sm border border-beige rounded-lg focus:border-sage focus:ring-2 focus:ring-sage/20 transition-all"
              data-testid="change-url-input"
            />
            <button type="button" onClick={handleUrlSearch} disabled={!urlInput.trim()}
              className="px-4 py-2 bg-sage text-white text-sm rounded-lg hover:bg-sage-dark transition-colors disabled:opacity-50 flex items-center gap-1.5"
              data-testid="change-url-search">
              <Search size={14} />
              Find
            </button>
          </div>
          <p className="text-[10px] text-gray-light">Paste a direct image URL (.jpg, .png) or a product page URL to find images.</p>

          <ImageGrid
            images={scrapedImages}
            selected={selectedScraped}
            onSelect={setSelectedScraped}
            onPreview={setPreviewUrl}
          />

          {selectedScraped && (
            <button type="button" onClick={handleUrlConfirm}
              className="w-full py-2 text-xs bg-sage text-white rounded-lg hover:bg-sage-dark transition-colors" data-testid="url-confirm">
              Use This Image
            </button>
          )}
        </div>
      )}

      {/* Footer — explicit cancel preserves the current image */}
      <div className="mt-5 pt-3 border-t border-beige flex justify-end">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-sm border border-beige text-charcoal rounded-lg hover:bg-cream transition-colors"
          data-testid="change-image-cancel"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
